/**
 * The cue "Change Page" box speaks in PRINTED page labels, not physical positions.
 *
 * Reported 1 Oct 2026 ("Harmony", 101 pages labelled A–H then 1–93): the box SHOWED
 * the label but Done READ what was typed as a physical index, so "10" sent the cue to
 * physical page 10 (label "2"); anything non-numeric became NaN, passed the range
 * check (NaN < 1 and NaN > n are both false), and was saved as pageNumber NaN, a page
 * nothing renders. Two cues vanished that way the same morning.
 *
 * Label-only on purpose: unlike resolvePageIndexFromDeepLink there is NO numeric-index
 * fallback. A number that is not a printed label is refused, because guessing between
 * "label 12" and "physical page 12" is exactly the bug. Same lookup as pagination.vue.
 */

/**
 * @param {string|number|null|undefined} draft  what the user typed
 * @param {Array<{ name?: string|number }>|null|undefined} pdfPageNumbers  page labels in physical order
 * @returns {number|null}  1-based physical page, or null if no page carries that label
 */
export function resolvePageLabel(draft, pdfPageNumbers) {
    if (draft == null) {
        return null;
    }
    const wanted = String(draft).trim();
    if (!wanted) {
        return null;
    }
    const pages = Array.isArray(pdfPageNumbers) ? pdfPageNumbers : [];
    const idx = pages.findIndex(p => p && p.name != null && String(p.name).trim() === wanted);
    return idx === -1 ? null : idx + 1;
}

/** The printed label of a 1-based physical page, or '' if there is none. */
export function labelForPage(pdfPageNumbers, pageNumber) {
    const pages = Array.isArray(pdfPageNumbers) ? pdfPageNumbers : [];
    const page = pages[Number(pageNumber) - 1];
    return page && page.name != null ? String(page.name) : '';
}

/**
 * The page a CSV export writes for a cue: the printed label, or the physical page
 * number when that page has no label. The same rule List view shows on screen.
 */
export function pageForExport(pdfPageNumbers, pageNumber) {
    const label = labelForPage(pdfPageNumbers, pageNumber);
    if (label) {
        return label;
    }
    return pageNumber != null ? String(pageNumber) : '';
}

/**
 * Is this a page number a cue may be saved with: a whole number >= 1 (and <= total,
 * when a total is known)? Accepts numeric strings so existing cues stored that way are
 * not blocked from unrelated edits; refuses NaN, decimals, blanks and non-numbers.
 */
export function isValidPageNumber(value, total) {
    if (value == null || (typeof value === 'string' && !value.trim())) {
        return false;
    }
    const n = Number(value);
    if (!Number.isInteger(n) || n < 1) {
        return false;
    }
    return !(Number.isInteger(total) && total > 0 && n > total);
}

/**
 * Defence in depth for every cue write: a write that CARRIES pageNumber must carry a real
 * one. Writes without pageNumber (title edits, field edits) pass untouched.
 * @returns {{ ok: boolean, reason?: string }}
 */
export function checkCuePageWrite(cue) {
    if (!cue || !Object.prototype.hasOwnProperty.call(cue, 'pageNumber')) {
        return { ok: true };
    }
    if (isValidPageNumber(cue.pageNumber)) {
        return { ok: true };
    }
    return {
        ok: false,
        reason: `Refusing to save cue ${cue.id || '(no id)'} to page ${String(cue.pageNumber)}: not a page number`
    };
}

export default { resolvePageLabel, labelForPage, pageForExport, isValidPageNumber, checkCuePageWrite };


// WEBPACK FOOTER //
// ./src/lib/cue-page.js